import { Builder } from '@builder.io/react';
import cn from 'classnames';
import capitalize from 'lodash-es/capitalize';
import { FC } from 'react';

import {
  ALLOW_BACKGROUND_COLORS,
  BackgroundClassMap,
  COLOR_NAME_PALE_GRAY,
  COLOR_NAME_WHITE,
} from '@/shared/constants/colors';
import {
  actionButtonInput,
  backgroundColorInput,
  componentLinkIdInput,
} from '@/shared/inputs';
import { getBuilderSpacingClass } from '@/shared/spacing';
import { SharedBuilderComponent } from '@/shared/types';
import { getBuilderIOClassName } from '@/shared/utils/getBuilderIOClassName';

import { ActionVariant, BaseAction } from '../Action';
import { Media, MediaInput, mediaInput } from '../Media';
import sharedStyle from '../shared.module.scss';
import { Content, MOCK_DESCRIPTION } from './Content';
import style from './Highlight.module.scss';

export enum HighlightType {
  STANDARD = 'standard',
  SECONDARY = 'secondary',
}

interface BuilderInput extends SharedBuilderComponent, MediaInput {
  componentLinkId?: string;
  type?: HighlightType;
  headline?: string;
  description?: string;
  highlightBackgroundClassName: string;
  action: BaseAction;
  actionVariant?: ActionVariant;
}

type Props = BuilderInput & {
  locale: string;
};

export const Highlight: FC<Props> = ({
  componentLinkId,
  type = HighlightType.STANDARD,
  headline,
  description,
  action,
  actionVariant,
  mediaType,
  mediaCaption,
  video,
  image,
  imageLink,
  imageLinkTarget,
  youtube,
  attributes,
  builderBlock,
  builderState,
  highlightBackgroundClassName,
  locale,
}) => {
  const builderStyles = getBuilderIOClassName(attributes);
  const spacingStyles = getBuilderSpacingClass(builderBlock, builderState);

  // When open page in editor or LivePreview mode, Builder doesn't run dataFetcher
  // causing locale value undefined. So, we use builderState locale value when
  // using component in editor and leave the locale when we generate static content
  const blockLocale = locale || builderState?.context.locale;

  return (
    <section
      data-testid="highlight"
      {...attributes}
      id={componentLinkId}
      className={cn(sharedStyle.section, builderStyles, spacingStyles)}
    >
      <div
        className={cn('u-wrapper', highlightBackgroundClassName, {
          'u-p-h-xl u-p-h-md-xxl-6 u-p-h-lg-xxl-6':
            type === HighlightType.STANDARD,
        })}
      >
        <div
          className={cn('u-flex', style.highlight, {
            'u-p-v-xl u-p-v-md-xxl-6 u-p-v-lg-xxl-6':
              type === HighlightType.STANDARD,
          })}
        >
          <Media
            className={style.media}
            containerClassName={style.container}
            locale={blockLocale}
            mediaType={mediaType}
            mediaCaption={mediaCaption}
            video={video}
            image={image}
            imageLink={imageLink}
            imageLinkTarget={imageLinkTarget}
            youtube={youtube}
          />
          <Content
            headline={headline}
            description={description}
            locale={blockLocale}
            action={{ ...action, variant: actionVariant, locale: blockLocale }}
          />
        </div>
      </div>
    </section>
  );
};

export function dataFetcher(locale: string) {
  return async () => ({
    locale,
  });
}

export const NAME = 'Highlight';

export function componentRegister() {
  Builder.registerComponent(Highlight, {
    name: NAME,
    inputs: [
      componentLinkIdInput(),
      {
        name: 'type',
        friendlyName: 'Type',
        type: 'string',
        defaultValue: HighlightType.STANDARD.toString(),
        enum: Object.values(HighlightType).map((val) => ({
          label: capitalize(val),
          value: val,
        })),
      },
      {
        name: 'headline',
        friendlyName: 'HeadLine',
        type: 'CatawikiEditor',
        defaultValue:
          'Title <span class="u-typography-h2-serif u-color-brand">Text</span>',
      },
      {
        name: 'description',
        friendlyName: 'Description',
        type: 'CatawikiEditor',
        defaultValue: MOCK_DESCRIPTION,
      },
      {
        name: 'highlightBackgroundClassName',
        friendlyName: 'Background Color',
        type: 'string',
        defaultValue: BackgroundClassMap[COLOR_NAME_PALE_GRAY],
        enum: ALLOW_BACKGROUND_COLORS.map((name) => ({
          label: capitalize(name),
          value: BackgroundClassMap[name],
        })),
      },
      backgroundColorInput(COLOR_NAME_WHITE, { isHidden: true }),
      ...actionButtonInput({}),
      ...mediaInput({ showMobileSource: false }),
    ],
    noWrap: true,
  });
  Builder.registerComponent(Highlight, {
    name: `v2:${NAME}`,
    inputs: [
      componentLinkIdInput(),
      {
        name: 'type',
        friendlyName: 'Type',
        type: 'string',
        defaultValue: HighlightType.STANDARD.toString(),
        enum: Object.values(HighlightType).map((val) => ({
          label: capitalize(val),
          value: val,
        })),
      },
      {
        name: 'headline',
        friendlyName: 'HeadLine',
        type: 'html',
        defaultValue:
          'Title <span class="u-typography-h2-serif u-color-brand">Text</span>',
      },
      {
        name: 'description',
        friendlyName: 'Description',
        type: 'html',
        defaultValue: MOCK_DESCRIPTION,
      },
      {
        name: 'highlightBackgroundClassName',
        friendlyName: 'Background Color',
        type: 'string',
        defaultValue: BackgroundClassMap[COLOR_NAME_PALE_GRAY],
        enum: ALLOW_BACKGROUND_COLORS.map((name) => ({
          label: capitalize(name),
          value: BackgroundClassMap[name],
        })),
      },
      backgroundColorInput(COLOR_NAME_WHITE, { isHidden: true }),
      ...actionButtonInput({ useCatawikiPlainEditor: false }),
      ...mediaInput({ showMobileSource: false, useCatawikiPlainEditor: false }),
    ],
    noWrap: true,
  });
  return NAME;
}

export const componentDataFetchers = (locale: string) => ({
  [NAME]: dataFetcher(locale),
  [`v2:${NAME}`]: dataFetcher(locale),
});
